import {
  Autocomplete,
  Box,
  Button,
  Card,
  Center,
  Container,
  Flex,
  Grid,
  Image,
  Loader,
  SimpleGrid,
  Text,
  TextInput,
  createStyles,
  rem,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { IconCamera } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  updateProfile,
  updateProfilePhoto,
} from "../../../../services/profile";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import VerifyPassword from "../../../../components/VerifyPassword";
import { showError, showSuccess } from "../../../../utils";
import { uploadFileToS3Api } from "../../../../utils/PresignedUrlFunction";
import { getSignupAddressByPlaceId, SignupsearchAddress } from "../../../../services";

const extractAddressFields = (res) => {
  const components = res?.result?.address_components || [];
  const formattedAddress = res?.result?.formatted_address || "";

  const get = (type, short = false) =>
    components.find((c) => c.types.includes(type))?.[
      short ? "short_name" : "long_name"
    ] || "";

  const city = get("locality") || get("administrative_area_level_2") || "";
  const state = get("administrative_area_level_1", true) || "";
  const postal_code = get("postal_code") || "";
  const countryFull = get("country", false) || "";

  let line1 = formattedAddress;
  [postal_code, state, city, countryFull].forEach((val) => {
    if (val)
      line1 = line1.replace(
        new RegExp(`\\s*,?\\s*${val}\\s*,?\\s*`, "g"),
        " "
      );
  });
  line1 = line1
    .replace(/\s*,\s*/g, ", ")
    .replace(/,+/g, ",")
    .replace(/^,|,$/, "")
    .trim()
    .replace(/\s+/g, " ");

  return {
    line1: res?.result?.name || line1,
    city,
    state,
    postal_code,
  };
};

const useStyles = createStyles((theme) => ({
  wrapper: {
    marginTop: 40,
    marginBottom: 40,
    borderWidth: 1,
    borderRadius: 15,
    borderStyle: "solid",
    borderColor: "#FFDEABEE",
    display: "flex",
    flexDirection: "row",
    "& .mantine-Container-root": {
      padding: "0px !important",
    },
  },
  camera: {
    position: "absolute",
    marginTop: 75,
    marginLeft: 75,
    background: "#FFBA45",
    borderRadius: "50%",
    padding: 5,
    [`&:hover`]: {
      cursor: "pointer",
    },
  },
  box: {
    width: 280,
    paddingTop: 47,
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderRightColor: "#F2F2F2",
    textAlign: "center",
    "@media (max-width :40em)": {
      maxWidth: "100%",
      width: "100%",
    },
  },
  btn: {
    [`&:hover`]: {
      cursor: "pointer",
    },
  },
  inputIcon: {
    borderRightWidth: 1,
    borderRigthColor: "#CBCBCB",
    borderRightStyle: "solid",
    paddingRight: 5,
  },
  changepwd: {
    position: "absolute",
    color: "#FFBA45",
    fontFamily: "Inter",
    fontSize: rem(14),
    marginLeft: "auto",
    textDecoration: "underline",
    paddingBottom: 10,
    marginTop: 9,
  },
  button: { width: 122, fontFamily: "Inter", fontSize: 16, fontWeight: 500 },
  drop: {
    display: "none",
    "& .mantine-Image-image": {
      border: "2px solid green",
    },
  },
  wrapperContianer: {
    "@media (max-width :40em)": {
      display: "flex",
      flexDirection: "column",
    },
  },
  inputContainer: {
    margin: "0 1rem",
    "@media (max-width :40em)": {
      display: "flex",
      flexDirection: "column",
      padding: "0 1rem",
      paddingBottom: "4rem",
    },
  },
  dropImage: {
    img: {
      borderRadius: "50%",
    },
  },
}));

const MyProfile = (props) => {
  const { classes } = useStyles();
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoLoading, setSelectedPhotoLoading] = useState(false);
  const { profile, saveProfileLoading } = useSelector(
    (state) => state?.profile
  );
  const [objForUpdate, setObjForUpdate] = useState(null);
  const openRef = useRef();
  const navigate = useNavigate();

  // Address autocomplete state
  const [streetQuery, setStreetQuery] = useState(profile?.user?.address || "");
  const [addressOptions, setAddressOptions] = useState([]);
  const placeIdMap = useRef({});
  const debounceTimer = useRef(null);

  const form = useForm({
    initialValues: {
      email: profile?.user?.email,
      name: profile?.user?.name,
      street: profile?.user?.address || "",
      city: profile?.user?.city || "",
      state: profile?.user?.state || "",
      zipCode: profile?.user?.zipcode || "",
    },
    validate: {
      name: (value) => (value ? null : "Required"),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });

  const handleStreetChange = (value) => {
    setStreetQuery(value);
    form.setFieldValue("street", value);

    clearTimeout(debounceTimer.current);
    if (!value.trim()) {
      setAddressOptions([]);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      const results = await SignupsearchAddress(value);
      if (!Array.isArray(results)) return;
      placeIdMap.current = {};
      const labels = results.map((r) => {
        placeIdMap.current[r.formatted_address] = r.place_id;
        return r.formatted_address;
      });
      setAddressOptions(labels);
    }, 1000);
  };

  const handleAddressSelect = async (label) => {
    setStreetQuery(label);
    const place_id = placeIdMap.current[label];
    if (!place_id) return;

    const data = await getSignupAddressByPlaceId(place_id);
    if (!data) return;

    const { line1, city, state, postal_code } = extractAddressFields(data);
    form.setFieldValue("street", line1);
    form.setFieldValue("city", city);
    form.setFieldValue("state", state);
    form.setFieldValue("zipCode", postal_code);
    setStreetQuery(line1);
    setAddressOptions([]);
  };

  const onFinish = async (values) => {
    let obj = {
      name: values?.name,
      address: values?.street,
      city: values?.city,
      state: values?.state,
      zipcode: values?.zipCode,
    };
    try {
      if (selectedPhoto) {
        const response = await updateProfilePhoto({ files: selectedPhoto });
        showSuccess(response?.message);
      }

      if (values?.email != profile?.user?.email) {
        open();
        obj.email = values?.email;
        setObjForUpdate({
          name: values?.name,
          oldEmail: profile?.user?.email,
          newEmail: values?.email,
        });
      } else {
        updateProfile(obj, navigate);
      }
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    }
  };

  // const onChangePhoto = async (files) => {
  //   if (!files?.length) return;
  //   const file = files[0];

  //   try {
  //     // upload to s3
  //     const result = await uploadFileToS3Api({ file });
  //     console.log("🚀 ~ onChangePhoto ~ result:", result);

  //     // upload to db
  //     const response = await updateProfilePhoto({ files: result });
  //     console.log("🚀 ~ onChangePhoto ~ response:", response);

  //     showSuccess(response?.message);
  //   } catch (error) {
  //     console.error("Upload failed", error);
  //     showError(error?.message || "Upload failed");
  //   }
  // };

  const handlePhotoChange = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    try {
      setSelectedPhotoLoading(true);
      const result = await uploadFileToS3Api({ file });
      setSelectedPhoto(result);
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    } finally {
      setSelectedPhotoLoading(false);
    }
  };

  // const onChangePhoto = (files) => {
  //   if (files?.length > 0) {
  //     const formData = new FormData();
  //     formData.append("file", files[0]);
  //     updateProfilePhoto(formData);
  //   }
  // };

  return (
    <>
      <form
        onSubmit={form?.onSubmit((values) => onFinish(values))}
        className={classes.profileForm}
      >
        <Card className={classes.wrapper} p={0}>
          <Container fluid sx={{ width: "100%" }}>
            <Grid columns={100} className={classes.wrapperContianer}>
              <Grid.Col span={30} className={classes.box}>
                <Center>
                  <Dropzone
                    onDrop={(files) => handlePhotoChange(files)}
                    className={classes.drop}
                    openRef={openRef}
                    accept={MIME_TYPES.jpeg | MIME_TYPES.png}
                  ></Dropzone>

                  {selectedPhotoLoading && !selectedPhoto ? (
                    <Flex
                      h={111}
                      w={111}
                      align="center"
                      justify="center"
                      sx={{ borderRadius: "50%" }}
                    >
                      <Loader color="#FFBA45" />
                    </Flex>
                  ) : (
                    <Image
                      src={
                        selectedPhoto?.uploadURL ||
                        profile?.user?.photo ||
                        "/assets/images/avatar.png"
                      }
                      alt=""
                      width={111}
                      height={111}
                      onClick={() => openRef.current?.()}
                      className={classes.dropImage}
                    />
                  )}
                  <IconCamera
                    color="white"
                    className={classes.camera}
                    onClick={() => openRef.current?.()}
                  />
                </Center>
                <Box sx={{ margin: 20 }}>
                  <Text ff={"Inter"} fw={700} fz={24}>
                    {profile?.user?.name}
                  </Text>
                  <Text ff={"Inter"} fw={600} fz={18} c={"#979797"} mt={10}>
                    {profile?.user?.position}
                  </Text>
                </Box>
              </Grid.Col>
              <Grid.Col span={"auto"} mr={5} mt={30}>
                <Container fluid sx={{ width: "100%" }}>
                  <SimpleGrid cols={1} className={classes.inputContainer}>
                    <TextInput
                      label={"Name*"}
                      size="md"
                      icon={
                        <img
                          src={"/assets/images/profile.png"}
                          alt=""
                          className={classes.inputIcon}
                        />
                      }
                      {...form.getInputProps("name")}
                    />
                    <TextInput
                      label={"Email"}
                      size="md"
                      icon={
                        <img
                          src={"/assets/images/sms.png"}
                          alt=""
                          className={classes.inputIcon}
                        />
                      }
                      {...form.getInputProps("email")}
                    />
                    <Autocomplete
                      label={"Address"}
                      placeholder="Start typing to search your address…"
                      size="md"
                      value={streetQuery}
                      onChange={handleStreetChange}
                      onItemSubmit={(item) => handleAddressSelect(item.value)}
                      data={addressOptions}
                      error={form.errors.street}
                      filter={() => true}
                    />
                    <SimpleGrid cols={3} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mb={20}>
                      <TextInput
                        label={"City"}
                        placeholder="City"
                        size="md"
                        {...form.getInputProps("city")}
                      />
                      <TextInput
                        label={"State"}
                        placeholder="State"
                        size="md"
                        {...form.getInputProps("state")}
                      />
                      <TextInput
                        label={"Zip Code"}
                        placeholder="Zip Code"
                        size="md"
                        {...form.getInputProps("zipCode")}
                      />
                    </SimpleGrid>
                  </SimpleGrid>
                </Container>
              </Grid.Col>
            </Grid>
          </Container>
        </Card>
        <SimpleGrid
          cols={2}
          sx={{ margin: "auto", marginTop: 25, marginBottom: 10 }}
        >
          <Button
            ml={"auto"}
            color="yellow"
            variant="light"
            className={classes.button}
            onClick={() => {
              setSelectedPhoto(null);
              navigate("/home/dashboard");
            }}
          >
            Cancel
          </Button>
          <Button
            mr={"auto"}
            type="submit"
            color="yellow"
            className={classes.button}
            loading={saveProfileLoading}
          >
            Save
          </Button>
        </SimpleGrid>
      </form>
      <VerifyPassword
        onclose={close}
        opened={opened}
        data={profile?.user}
        objForUpdate={objForUpdate}
      />
    </>
  );
};

export default MyProfile;
